"use client";

import { Checkbox } from "@/components/shared/form";
import { Dialog, TitleH2 } from "@/components/shared/public";
import { Info } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import DesignContainer from "./DesignContainer";

const DESIGN_COPY_KEYS = {
  black: "black",
  "black and white": "blackWhite",
  gray: "gray",
  modern: "modern",
} as const;

type DesignName = keyof typeof DESIGN_COPY_KEYS;

const DesignHolder = () => {
  const tC = useTranslations("common");
  const tH = useTranslations("floorPlanDesigns.designContainer");

  const [currentDesign, setCurrentDesign] = useState<DesignName>("black");
  const [furnishings, setFurnishings] = useState(true);
  const [roomNames, setRoomNames] = useState(false);
  const [squareMetersPerRoom, setSquareMetersPerRoom] = useState(false);
  const [scale, setScale] = useState(false);
  const [northArrow, setNorthArrow] = useState(false);
  const [entrance, setEntrance] = useState(true);
  const [kitchenEquipment, setKitchenEquipment] = useState(true);
  const [bathroomEquipment, setBathroomEquipment] = useState(true);

  const copyKey = DESIGN_COPY_KEYS[currentDesign];

  return (
    <div className="gs--design-gallery gs--page-column pt-0 pb-16">
      <div className="mt-8 flex flex-col items-center gap-6 lg:grid lg:grid-cols-[minmax(0,550px)_1fr] lg:items-stretch lg:gap-10">
        {/* Left column: preview + thumbnails */}
        <div className="gs--design-gallery__left w-full max-w-[550px] shrink-0 lg:min-h-0">
          <DesignContainer
            furnishings={furnishings}
            roomNames={roomNames}
            squareMetersPerRoom={squareMetersPerRoom}
            scale={scale}
            northArrow={northArrow}
            entrance={entrance}
            kitchenEquipment={kitchenEquipment}
            bathroomEquipment={bathroomEquipment}
            currentDesign={currentDesign}
            onDesignChange={(name) => {
              if (name in DESIGN_COPY_KEYS) {
                setCurrentDesign(name as DesignName);
              }
            }}
          />
        </div>

        {/* Right column: title + info + checkboxes */}
        <div className="gs--design-gallery__right w-full lg:flex lg:flex-1 lg:flex-col">
          <div className="mb-5 flex w-full items-center justify-between gap-3">
            <TitleH2 title={tH(`${copyKey}.title`)} />
            <Dialog
              trigger={
                <Info
                  className="text-primary lg:hover:bg-primary shrink-0 cursor-pointer rounded-full p-1.5 transition-colors duration-200 lg:hover:text-white"
                  size={30}
                />
              }
              title={tH(`${copyKey}.title`)}
              description={tH(`${copyKey}.description`)}
              subDescription={tH(`${copyKey}.subDescription`)}
            />
          </div>

          <div className="gs--design-gallery__options grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex-1 lg:grid-cols-1">
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="furnishings"
                label={tC("furnishings")}
                checked={furnishings}
                onCheckedChange={() => setFurnishings(!furnishings)}
              />
            </div>
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="roomNames"
                label={tC("roomNames")}
                checked={roomNames}
                onCheckedChange={() => setRoomNames(!roomNames)}
              />
            </div>
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="northArrow"
                label={tC("northArrow")}
                checked={northArrow}
                onCheckedChange={() => setNorthArrow(!northArrow)}
              />
            </div>
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="entrance"
                label={tC("entrance")}
                checked={entrance}
                onCheckedChange={() => setEntrance(!entrance)}
              />
            </div>
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="kitchenEquipment"
                label={tC("kitchenEquipment")}
                checked={kitchenEquipment}
                onCheckedChange={() => setKitchenEquipment(!kitchenEquipment)}
              />
            </div>
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="bathroomEquipment"
                label={tC("bathroomEquipment")}
                checked={bathroomEquipment}
                onCheckedChange={() => setBathroomEquipment(!bathroomEquipment)}
              />
            </div>
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="squareMetersPerRoom"
                label={tC("squareMetersPerRoom")}
                checked={squareMetersPerRoom}
                onCheckedChange={() =>
                  setSquareMetersPerRoom(!squareMetersPerRoom)
                }
              />
            </div>
            <div className="gs--design-gallery__option-row">
              <Checkbox
                name="scale"
                label={tC("scale")}
                checked={scale}
                onCheckedChange={() => setScale(!scale)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignHolder;
